import { $$, on } from "../../utils/dom";

// Handles filtering + URL syncing for ServiceCatalogListBlock (data-service-catalog-list-block)
// and CategoryPageListBlock (data-category-list-block). Both blocks share the CategoriesPartialRouter.
const blockSelector =
  '[data-service-catalog-list-block="true"], [data-category-list-block="true"]';

const toIdSet = (values: string[]): Set<string> =>
  new Set(values.filter((value) => value.length > 0));

export const updateServiceCategoryCounts = (block: HTMLElement): void => {
  block
    .querySelectorAll<HTMLElement>(".m-serviceCatalogListBlock__category")
    .forEach((category) => {
      const visibleServiceCount = Array.from(
        category.querySelectorAll<HTMLElement>(".m-services__list-item")
      ).filter(
        (service) =>
          !service.classList.contains("is-hidden") &&
          !service.classList.contains("is-hidden--non-standard")
      ).length;

      const count = category.querySelector<HTMLElement>(
        ".m-services__category-count"
      );
      if (count) {
        count.textContent = visibleServiceCount.toString();
      }
    });
};

/**
 * Parses the comma-separated descendant category IDs stored on a filter checkbox.
 * Returns an empty set when the attribute is absent (backward-compatible with existing blocks).
 */
const getDescendantIds = (checkbox: HTMLInputElement): Set<string> => {
  const raw = checkbox.dataset.serviceCatalogFilterDescendants ?? "";
  if (!raw) return new Set();
  return toIdSet(raw.split(","));
};

const applyFilter = (block: HTMLElement): void => {
  const checkedCheckboxes = Array.from(
    block.querySelectorAll<HTMLInputElement>(
      ".m-serviceCatalogListBlock__checkbox:checked"
    )
  );

  const directCheckedIds = toIdSet(
    checkedCheckboxes.map((cb) => cb.dataset.serviceCatalogFilter ?? "")
  );

  // Build the full visible set: directly-checked categories + all their descendants
  const visibleCategoryIds = new Set<string>(directCheckedIds);
  checkedCheckboxes.forEach((cb) => {
    getDescendantIds(cb).forEach((id) => visibleCategoryIds.add(id));
  });

  const categories = block.querySelectorAll<HTMLElement>(
    ".m-serviceCatalogListBlock__category"
  );

  categories.forEach((category) => {
    const categoryId = category.dataset.serviceCatalogCategory ?? "";
    // Ancestors are stored as a comma-separated string; top-level categories have an empty string
    const ancestors = toIdSet(
      (category.dataset.serviceCatalogCategoryAncestors ?? "").split(",")
    );

    const isVisible =
      directCheckedIds.size === 0 ||
      visibleCategoryIds.has(categoryId) ||
      Array.from(directCheckedIds).some((id) => ancestors.has(id));

    category.classList.toggle("is-hidden", !isVisible);
  });

  // Flat-view support: individual service items carry their category IDs so they can
  // be hidden even when there is no wrapping category group (ServiceCatalogListFlatServices).
  const serviceItems = block.querySelectorAll<HTMLElement>(
    "[data-service-catalog-service-categories]"
  );

  const hiddenServiceIds = new Set<string>();

  serviceItems.forEach((item) => {
    const itemCategoryIds = toIdSet(
      (item.dataset.serviceCatalogServiceCategories ?? "").split(",")
    );

    const isVisible =
      directCheckedIds.size === 0 ||
      Array.from(itemCategoryIds).some((id) => visibleCategoryIds.has(id));

    item.classList.toggle("is-hidden", !isVisible);

    const serviceId = item.dataset.serviceCatalogService ?? "";
    if (!isVisible && serviceId) {
      hiddenServiceIds.add(serviceId);
    }
  });

  // Customization submenus deliberately don't carry the category attribute, but they
  // share `data-service-catalog-service` with their parent service item. Hide them
  // together with their parent so filtered-out services don't leak their submenu.
  const submenuItems = block.querySelectorAll<HTMLElement>(
    ".m-serviceCatalogListBlock__submenuItem[data-service-catalog-service]"
  );

  submenuItems.forEach((submenu) => {
    const serviceId = submenu.dataset.serviceCatalogService ?? "";
    submenu.classList.toggle("is-hidden", hiddenServiceIds.has(serviceId));
  });

  updateServiceCategoryCounts(block);
};

const syncUrl = (block: HTMLElement): void => {
  if (typeof window === "undefined" || !window.history?.replaceState) return;

  const baseUrl = block.dataset.serviceCatalogBaseUrl ?? "";
  const categoriesRoute = block.dataset.serviceCatalogCategoriesRoute ?? "";
  if (!baseUrl || !categoriesRoute) return;

  const segments = Array.from(
    block.querySelectorAll<HTMLInputElement>(
      ".m-serviceCatalogListBlock__checkbox:checked"
    )
  )
    .map((cb) => cb.dataset.serviceCatalogFilterSegment ?? "")
    .filter((segment) => segment.length > 0);

  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const newPath =
    segments.length === 0
      ? normalizedBase || "/"
      : `${normalizedBase}/${encodeURIComponent(categoriesRoute)}/${segments
          .map(encodeURIComponent)
          .join(",")}`;

  const newUrl = `${newPath}${window.location.search}${window.location.hash}`;
  if (newUrl === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    return;
  }

  window.history.replaceState(null, "", newUrl);
};

export const onContentLoaded = (): void => {
  const blocks = $$(blockSelector);
  if (!blocks) {
    return;
  }

  blocks.forEach((block) => {
    block
      .querySelectorAll<HTMLElement>(".m-serviceCatalogListBlock__checkbox")
      .forEach((input) => {
        on(input, "change", () => {
          applyFilter(block);
          syncUrl(block);
        });
      });

    // Honor checkboxes that are pre-checked on initial render (e.g. server-side state).
    applyFilter(block);
  });
};

if (process.env.NODE_ENV !== "development") {
  on(document, "DOMContentLoaded", onContentLoaded);
}
